import React, { useEffect, useMemo, useRef } from 'react';
import {
  IonPage,
  IonContent,
  IonItem,
  IonLabel,
  IonInput,
  IonText,
  IonIcon,
  useIonViewWillLeave,
} from '@ionic/react';
import { mailOutline } from 'ionicons/icons';
import { FooterDock, FooterDockRow, SmartCaseButton } from '@/modules/footer';
import { useUserContext } from '@/modules/user/useUserContext';
import { useProfileWizardContext } from '@/contexts/ProfileWizardContext';
import { ProfileWizardLogic } from './logic';
import ProfileWizardHeader from './ProfileWizardHeader';
import { useAppToast } from '@/hooks/useAppToast';
import EmailVerificationModal from '@/modules/idv/EmailVerificationModal';
import { ClaimsLogic } from '@/modules/idv';
import { apiCall } from '@/util/api';
import { useTopicState } from '@/modules/topic/TopicStateContext';
import { pxToRem } from '@/modules/config/constants';

const EmailStep: React.FC = () => {
  const {
    email: contextEmail,
    claims,
    setEmail: setContextEmail,
    setPendingEmail: setContextPendingEmail,
    refreshUserContext,
    hasUserId: hasContextUserId,
  } = useUserContext();
  const {
    email,
    setEmail,
    originalEmail,
    setOriginalEmail,
    showEmailModal,
    setShowEmailModal,
    urlProvidedOrigin,
    routerProvidedOrigin,
    navigateWithOrigin,
    navigateToStep,
    progress,
  } = useProfileWizardContext();
  const { dispatch: dispatchActionState } = useTopicState();

  useIonViewWillLeave(() => {
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
  });

  // Tracks a just-completed verification so the modal's trailing dismiss event
  // (which reports 'cancelled') doesn't revert the email we just committed.
  const verificationSucceededRef = useRef(false);

  // Determine variant based on context (similar to Step1CurrentProfile pattern)
  const variant = useMemo<
    'no-email' | 'unverified-email' | 'verified-email'
  >(() => {
    if (!contextEmail) return 'no-email';
    if (ClaimsLogic.isEmailVerified(claims)) return 'verified-email';
    return 'unverified-email';
  }, [contextEmail, claims]);

  // The wizard's email buffer is seeded from UserContext once, when the router
  // first loads. A returning user who is only identified to their profile on the
  // phone step gets their stored email AFTER that seed, so the buffer misses it
  // and this field would render empty. Pull it in once it's available — but only
  // while the field is untouched (both the buffer and its change-detection
  // snapshot still empty), so we never overwrite an in-progress edit or a value
  // the user deliberately cleared.
  useEffect(() => {
    if (contextEmail && email === '' && originalEmail === '') {
      setEmail(contextEmail);
      setOriginalEmail(contextEmail);
    }
  }, [contextEmail, email, originalEmail, setEmail, setOriginalEmail]);

  // Send the user to the phone step to verify their phone. Any entered address
  // stays staged in the wizard, so when they return this step still shows it.
  const handleVerifyPhoneFirst = () => {
    navigateWithOrigin('/profile/steps/phone', 'back');
  };

  const isEmailValid = (): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email.trim());
  };

  const isEmailEdited = (): boolean => {
    return email.toLowerCase().trim() !== originalEmail.toLowerCase();
  };

  const saveEmailToDatabase = async (emailToSave: string | undefined) => {
    // Only save if user has a profile and email has changed
    if (!hasContextUserId) {
      return;
    }

    const normalizedNewEmail = emailToSave?.trim() || undefined;

    // Check if email has actually changed
    if (normalizedNewEmail === contextEmail) {
      return;
    }

    try {
      const requestBody = { email: normalizedNewEmail || null };
      const updateResponse = await apiCall(`/api/users/me`, {
        method: 'PATCH',
        body: JSON.stringify(requestBody),
      });

      if (!updateResponse.ok) {
        const errorText = await updateResponse.text();
        throw new Error(errorText || 'Failed to update email');
      }

      // Token is stored automatically by apiCall via applyTokenFromResponse

      await refreshUserContext();
    } catch (error) {
      // Log error but don't show toast for background saves
      // eslint-disable-next-line no-console
      console.error('Failed to save email change:', error);
    }
  };

  const handleVerify = async () => {
    // Defensive (the UI swaps the button for these cases, but never fire a
    // request the backend will 403): changing the email needs phone verification;
    // re-verifying the existing email needs some live verification.
    if (isEmailEdited() && !ClaimsLogic.isPhoneVerified(claims)) {
      handleVerifyPhoneFirst();
      return;
    }
    if (!isEmailEdited() && !ClaimsLogic.isPhoneOrEmailVerified(claims)) {
      handleVerifyPhoneFirst();
      return;
    }

    const trimmedEmail = email.trim();

    // Set the address PENDINGLY so the verification modal can use it, but it
    // is not committed to storage until verified — so reloading mid-verification
    // restores the prior address rather than this unverified edit.
    //
    // Email-level verification only needs the authenticated user and the address:
    // the phone step has already fixed the user's identity (migrating the profile
    // onto a new number when it changed), so the wizard phone always belongs to
    // the current user — there's no profile to create or session to switch here.
    // The backend rejects an address already owned by another profile with a 409.
    setContextPendingEmail(trimmedEmail);
    setShowEmailModal(true);
  };

  const handleRefreshVerification = () => {
    setShowEmailModal(true);
  };

  const handleSkip = async () => {
    // Setting a new email needs phone verification — the backend (both the IDV
    // path and PATCH /api/users/me) rejects it otherwise — so skipping a non-empty
    // change without a phone verification discards the edit rather than firing a
    // doomed save that would leave local state showing an address the server never
    // stored. Clearing the email, or skipping when phone-verified, still persists.
    if (
      isEmailEdited() &&
      email.trim() !== '' &&
      !ClaimsLogic.isPhoneVerified(claims)
    ) {
      setContextEmail(originalEmail.trim() || undefined);
      navigateWithOrigin('/profile/steps/summary', 'forward');
      return;
    }

    // When skipping email, save the current email value to database and UserContext
    const normalizedEmail = email.trim() || undefined;
    await saveEmailToDatabase(normalizedEmail);
    setContextEmail(normalizedEmail);
    navigateWithOrigin('/profile/steps/summary', 'forward');
  };

  const [presentToast] = useAppToast();

  // The new address is only committed once verified. If the user backs out,
  // snap the input and the app-wide context email back to the previously stored
  // value so the step never shows an unverified change (and the Verify button
  // label reverts from "Verify new email" to "Verify").
  const revertEmailToPrevious = () => {
    setEmail(originalEmail);
    setContextEmail(originalEmail.trim() || undefined);
  };

  const handleEmailVerificationClose = (reason?: string) => {
    setShowEmailModal(false);

    // A successful verification commits the new email. Closing the modal on
    // success — and the dismiss event that fires right after — must not revert.
    if (reason === 'success') {
      verificationSucceededRef.current = true;
      return;
    }
    if (verificationSucceededRef.current) {
      verificationSucceededRef.current = false;
      return;
    }

    // Cancelled or timed out before verifying: restore the previous email.
    if (reason === 'cancelled' || reason === 'expired') {
      revertEmailToPrevious();
    }

    if (reason === 'expired') {
      presentToast({
        message: 'Verification code expired. Please try verifying again.',
        color: 'warning',
        duration: 5000,
      });
    }
  };

  const handleEmailVerificationSuccess = () => {
    verificationSucceededRef.current = true;

    // Commit the now-verified address: write it through to the committed EMAIL
    // key and clear the pending stash (the modal's confirm already committed
    // it on the backend).
    const verifiedEmail = email.trim();
    if (verifiedEmail) {
      setContextEmail(verifiedEmail);
    }

    // If this verification changed the user's email, swap their old address for
    // the new one in any in-memory action "listing manager" details, so actions
    // they manage reflect the change without waiting for a refetch (#288).
    const newEmail = email.trim();
    if (
      originalEmail.trim() !== '' &&
      newEmail !== '' &&
      originalEmail.toLowerCase() !== newEmail.toLowerCase()
    ) {
      dispatchActionState({
        type: 'REPLACE_MANAGER_EMAIL',
        oldEmail: originalEmail,
        newEmail,
      });
    }

    setShowEmailModal(false);
    navigateWithOrigin('/profile/steps/summary', 'forward');
  };

  const getEmailVerificationContinuePath = (): string => {
    // After email verification, user should advance to summary step
    const summaryStep = '/profile/steps/summary';

    // Always include origin query param, defaulting to '/' if routerProvidedOrigin is not available
    const origin = routerProvidedOrigin || urlProvidedOrigin || '/';
    const continuePath = `${summaryStep}?origin=${encodeURIComponent(origin)}`;

    return continuePath;
  };

  const emailExp = ClaimsLogic.emailVerifExpiration(claims);
  const expirationText = emailExp ? emailExp.formattedDate : null;

  const emailEdited = isEmailEdited();

  // When the user changes a pre-existing email to a different address, verifying
  // replaces their stored email — label the action so that's clear. A first-time
  // email (no stored original) stays a plain "Verify".
  const isVerifyingNewEmail = originalEmail.trim() !== '' && emailEdited;

  // Two distinct email-verification gates, mirroring the backend:
  //
  //   • CHANGING/setting a new address requires a live PHONE verification. The
  //     security questions must never unlock a change, so a lost-SIM (or merely
  //     email-verified) user is sent to verify their phone — nothing else.
  const mustVerifyPhoneToChange =
    hasContextUserId && emailEdited && !ClaimsLogic.isPhoneVerified(claims);

  //   • RE-VERIFYING the existing address needs a live verification too. A
  //     user with none is steered to the phone step to prove their identity.
  const mustVerifyPhoneToReverify =
    hasContextUserId &&
    !emailEdited &&
    originalEmail.trim() !== '' &&
    !ClaimsLogic.isPhoneOrEmailVerified(claims);

  // The "simple verify" variant: the address is already verified and unedited,
  // so the step only offers Refresh + Next. Its complement (!isSimpleVerifyVariant)
  // is the compose flow — entering or editing an address, where the primary
  // action verifies the change.
  const isSimpleVerifyVariant = variant === 'verified-email' && !emailEdited;

  return (
    <IonPage>
      <ProfileWizardHeader
        title={ProfileWizardLogic.titleForStep('email')}
        progress={progress}
        onBack={() =>
          navigateToStep(ProfileWizardLogic.previousStep('email'), 'back')
        }
      />
      <IonContent>
        <div style={{ padding: '20px 20px 120px' }}>
          <IonText>
            {variant === 'no-email' && (
              <p style={{ fontSize: pxToRem(16), marginBottom: '20px' }}>
                Email address is not required, but a verified email will let you
                do fancier stuff like registering a new topic.
              </p>
            )}
            {variant === 'unverified-email' && (
              <p style={{ fontSize: pxToRem(16), marginBottom: '20px' }}>
                Here's the email address we have for you. Press Next to verify.
                Changed emails? Type the new one here.
              </p>
            )}
            {variant === 'verified-email' && (
              <p style={{ fontSize: pxToRem(16), marginBottom: '20px' }}>
                This is the verified email address we have for you.
                {expirationText &&
                  ` Verification will expire in ${expirationText}.`}{' '}
                Changed emails? Type the new one here.
              </p>
            )}
          </IonText>
          <IonItem>
            <IonLabel position="stacked">
              <IonIcon icon={mailOutline} style={{ marginRight: '5px' }} />
              Email
            </IonLabel>
            <IonInput
              type="email"
              value={email}
              onIonInput={e => {
                const inputValue = (e.detail.value as string) || '';
                // Update only the local wizard state while typing. The
                // address is not committed to context/storage until it's
                // verified (or the user explicitly Skips), so an
                // abandoned edit never overwrites the prior address.
                setEmail(inputValue);
              }}
              placeholder="your.email@example.com"
              autocomplete="email"
            />
          </IonItem>
          {/* Why the primary action is what it is. Changing the email needs
                  phone verification (security questions are NOT offered here);
                  re-verifying an existing address needs a live claim. */}
          {!isSimpleVerifyVariant && mustVerifyPhoneToChange && (
            <IonText>
              <p
                style={{
                  fontSize: pxToRem(13),
                  color: 'var(--ion-color-medium)',
                  margin: '8px 0 0',
                }}
              >
                To change the email on your account, verify your phone number
                first on the previous step.
              </p>
            </IonText>
          )}
          {!isSimpleVerifyVariant && mustVerifyPhoneToReverify && (
            <IonText>
              <p
                style={{
                  fontSize: pxToRem(13),
                  color: 'var(--ion-color-medium)',
                  margin: '8px 0 0',
                }}
              >
                To verify your email, verify your phone number on the previous
                step.
              </p>
            </IonText>
          )}
          {!isSimpleVerifyVariant &&
            !mustVerifyPhoneToChange &&
            !mustVerifyPhoneToReverify &&
            isVerifyingNewEmail && (
              <IonText>
                <p
                  style={{
                    fontSize: pxToRem(13),
                    color: 'var(--ion-color-medium)',
                    margin: '8px 0 0',
                  }}
                >
                  Once you verify, we'll update the address shown for any topic
                  listings you manage.
                </p>
              </IonText>
            )}
        </div>
      </IonContent>

      <FooterDock background="transparent">
        {/* Secondary actions flank the single primary, never the toolbar. */}
        {variant === 'verified-email' && !emailEdited && (
          <FooterDockRow>
            <SmartCaseButton
              expand="block"
              onClick={handleRefreshVerification}
              color="secondary"
              style={{ margin: 0 }}
            >
              Refresh verification
            </SmartCaseButton>
          </FooterDockRow>
        )}
        {!isSimpleVerifyVariant && variant !== 'verified-email' && (
          <FooterDockRow>
            <SmartCaseButton
              expand="block"
              onClick={handleSkip}
              color="medium"
              style={{ margin: 0 }}
            >
              Skip email for now
            </SmartCaseButton>
          </FooterDockRow>
        )}
        <FooterDockRow landmark>
          {isSimpleVerifyVariant ? (
            // Verified & unedited: commit the current address and continue.
            <SmartCaseButton
              expand="block"
              onClick={async () => {
                const normalizedEmail = email.trim() || undefined;
                await saveEmailToDatabase(normalizedEmail);
                setContextEmail(normalizedEmail);
                navigateWithOrigin('/profile/steps/summary', 'forward');
              }}
              color="primary"
              data-testid="profile-wizard-next"
              style={{ margin: 0 }}
            >
              Next
            </SmartCaseButton>
          ) : mustVerifyPhoneToChange ? (
            <SmartCaseButton
              expand="block"
              onClick={handleVerifyPhoneFirst}
              color="primary"
              data-testid="profile-wizard-next"
              style={{ margin: 0 }}
            >
              Verify phone to change email
            </SmartCaseButton>
          ) : mustVerifyPhoneToReverify ? (
            <SmartCaseButton
              expand="block"
              onClick={handleVerifyPhoneFirst}
              color="primary"
              data-testid="profile-wizard-next"
              style={{ margin: 0 }}
            >
              Verify phone
            </SmartCaseButton>
          ) : (
            <SmartCaseButton
              expand="block"
              onClick={handleVerify}
              disabled={!isEmailValid()}
              color="primary"
              data-testid="profile-wizard-next"
              style={{ margin: 0 }}
            >
              {isVerifyingNewEmail ? 'Verify new email' : 'Verify'}
            </SmartCaseButton>
          )}
        </FooterDockRow>
      </FooterDock>

      <EmailVerificationModal
        isOpen={showEmailModal}
        onClose={handleEmailVerificationClose}
        onSuccess={handleEmailVerificationSuccess}
        continuePath={getEmailVerificationContinuePath()}
      />
    </IonPage>
  );
};

export default EmailStep;
